/**
 * FlashMessageManager.js
 *
 * Handles flash message display, auto-dismiss, and user interactions
 * - Auto-dismisses messages after 5 seconds
 * - Allows manual close via button click
 * - Provides accessibility features (ARIA labels, keyboard support)
 */

class FlashMessageManager {
    constructor(options = {}) {
        this.autoDismissDelay = options.autoDismissDelay || 5000;
        this.container = null;

        // Use element references as Map keys (guaranteed unique)
        this.timeouts = new Map();

        // Optional debug flag
        this.debug = !!options.debug;

        this.init();
    }

    log(...args) {
        if (this.debug) console.debug(...args);
    }

    /**
     * Initialize the flash message manager
     */
    init() {
        this.container = document.querySelector('.flash-messages-container');
        if (!this.container) {
            // Silent no-op is fine in a shared-bundle setup
            return;
        }

        const messageElements = this.container.querySelectorAll('.flash-message');
        messageElements.forEach(messageEl => {
            this.attachMessageListeners(messageEl);
            this.scheduleAutoDismiss(messageEl);
        });

        this.log('✅ FlashMessageManager initialized');
    }

    /**
     * Attach event listeners to a flash message
     */
    attachMessageListeners(messageEl) {
        const closeBtn = messageEl.querySelector('.flash-close');
        if (!closeBtn) return;

        closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.dismissMessage(messageEl);
        });

        closeBtn.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.dismissMessage(messageEl);
            }
        });
    }

    /**
     * Schedule auto-dismiss for a message after the specified delay
     */
    scheduleAutoDismiss(messageEl) {
        // Clear any existing timeout for this element
        const existingTimeout = this.timeouts.get(messageEl);
        if (existingTimeout) clearTimeout(existingTimeout);

        const timeoutId = setTimeout(() => {
            this.dismissMessage(messageEl);
        }, this.autoDismissDelay);

        this.timeouts.set(messageEl, timeoutId);
    }

    /**
     * Dismiss a flash message with animation
     */
    dismissMessage(messageEl) {
        const existingTimeout = this.timeouts.get(messageEl);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
            this.timeouts.delete(messageEl);
        }

        // If already removed, bail
        if (!messageEl || !messageEl.parentElement) return;

        messageEl.classList.remove('flash-message-show');
        messageEl.classList.add('flash-message-hide');

        setTimeout(() => {
            if (messageEl.parentElement) {
                messageEl.remove();
            }
        }, 300);
    }

    /**
     * Dismiss all messages
     */
    dismissAllMessages() {
        if (!this.container) return;
        const messageElements = this.container.querySelectorAll('.flash-message');
        messageElements.forEach(messageEl => this.dismissMessage(messageEl));
    }

    /**
     * Cancel auto-dismiss for a specific message (e.g., on hover)
     */
    cancelAutoDismiss(messageEl) {
        const existingTimeout = this.timeouts.get(messageEl);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
            this.timeouts.delete(messageEl);
        }
    }

    /**
     * Resume auto-dismiss for a specific message
     */
    resumeAutoDismiss(messageEl) {
        this.scheduleAutoDismiss(messageEl);
    }

    /**
     * Destroy the manager and clean up
     */
    destroy() {
        this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
        this.timeouts.clear();
        this.container = null;
        this.log('🛑 FlashMessageManager destroyed');
    }
}

// Auto-initialize when DOM is ready (idempotent)
function boot() {
    if (window.flashMessageManager) return;

    window.flashMessageManager = new FlashMessageManager({
        autoDismissDelay: 5000,
        debug: false,
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}

export default FlashMessageManager;
